/**
 * markdownToPlainTextPreview — flatten markdown to a single line of plain
 * text for compact previews (list rows, snippets) where rendering a full
 * markdown tree is DOM overkill and a raw string would leak syntax
 * characters.
 *
 * Deliberately conservative, regex-based stripping — NOT a markdown parser:
 * - links / images collapse to their text / alt
 * - `inline code`, **bold**, headings, blockquote and list markers are
 *   unwrapped
 * - underscore emphasis (`_em_`, `__strong__`) is left ALONE: descriptions
 *   routinely mention `snake_case` identifiers (ahoy_property names), and a
 *   regex cannot tell emphasis from an identifier without a parser
 * - newlines collapse to single spaces (the output renders on one line)
 *
 * A stray marker surviving is cosmetically harmless in a preview line; eating
 * part of an identifier is not — when in doubt this function leaves text
 * untouched.
 */
export function markdownToPlainTextPreview(markdown: string): string {
  return (
    markdown
      // Images before links (same bracket syntax, leading !): keep alt text.
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Links: keep the link text.
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      // Inline code: drop the backticks.
      .replace(/`([^`]+)`/g, '$1')
      // Bold then italic (asterisk forms only — see docstring re underscores).
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      // Line-leading structural markers: headings, blockquotes, list bullets,
      // ordered-list numbers.
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^>\s?/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // One line out: collapse all whitespace runs (incl. newlines).
      .replace(/\s+/g, ' ')
      .trim()
  );
}
